import { addWeeks } from "date-fns/addWeeks";
import { differenceInCalendarISOWeeks } from "date-fns/differenceInCalendarISOWeeks";
import { format } from "date-fns/format";
import { subWeeks } from "date-fns/subWeeks";

import { isoWeek, isoWeeksInYear, isoWeekYear, weekToDateRange } from "~/helpers/dateCalc";

export const WEEKS_BACK_DEFAULT = 2;

export const WEEKS_AHEAD_DEFAULT = 9;

export const WEEKS_AHEAD_NEXT_8 = 7;

export const SEASON_START_WEEK = 27;

export const CUSTOM_RANGE_MAX_WEEKS = 52;

export type CalendarWindowPreset = "CUSTOM" | "DEFAULT" | "NEXT_8" | "SEASON";

export interface IsoWeekPoint {
	isoWeekNumber: number;
	isoYear: number;
}

export interface CalendarWeek extends IsoWeekPoint {
	mondayDate: Date;
}

export interface CustomCalendarRange {
	end: Date;
	start: Date;
}

export interface CustomRangeBounds {
	maxDate?: Date;
	minDate?: Date;
}

export const toIsoWeekPoint = (date: Date): IsoWeekPoint => ({
	isoWeekNumber: isoWeek(date),
	isoYear: isoWeekYear(date)
});

const toCalendarWeek = (point: IsoWeekPoint): CalendarWeek => ({
	...point,
	mondayDate: weekToDateRange(point.isoWeekNumber, point.isoYear).start
});

const clampIsoWeek = (point: IsoWeekPoint): IsoWeekPoint => {
	const maxWeek = isoWeeksInYear(weekToDateRange(1, point.isoYear).start);
	return point.isoWeekNumber > maxWeek ? { ...point, isoWeekNumber: maxWeek } : point;
};

export const enumerateCalendarWeeks = (start: IsoWeekPoint, end: IsoWeekPoint): CalendarWeek[] => {
	const weeks: CalendarWeek[] = [];
	let cursor = weekToDateRange(start.isoWeekNumber, start.isoYear).start;
	const endDate = weekToDateRange(end.isoWeekNumber, end.isoYear).start;
	while (cursor <= endDate) {
		weeks.push(toCalendarWeek(toIsoWeekPoint(cursor)));
		cursor = addWeeks(cursor, 1);
	}
	return weeks;
};

export const resolveCalendarWeeks = (
	preset: CalendarWindowPreset,
	now: Date,
	custom?: CustomCalendarRange
): CalendarWeek[] => {
	const current = toIsoWeekPoint(now);
	if (preset === "CUSTOM") {
		if (!custom) return enumerateCalendarWeeks(current, current);
		return enumerateCalendarWeeks(toIsoWeekPoint(custom.start), toIsoWeekPoint(custom.end));
	}
	if (preset === "NEXT_8") return enumerateCalendarWeeks(current, toIsoWeekPoint(addWeeks(now, WEEKS_AHEAD_NEXT_8)));
	if (preset === "SEASON") {
		const seasonStartYear = current.isoWeekNumber >= SEASON_START_WEEK ? current.isoYear : current.isoYear - 1;
		const seasonStart = clampIsoWeek({ isoWeekNumber: SEASON_START_WEEK, isoYear: seasonStartYear });
		return enumerateCalendarWeeks(seasonStart, current);
	}
	return enumerateCalendarWeeks(
		toIsoWeekPoint(subWeeks(now, WEEKS_BACK_DEFAULT)),
		toIsoWeekPoint(addWeeks(now, WEEKS_AHEAD_DEFAULT))
	);
};

export const customRangeStartBounds = (customEnd: Date | undefined): CustomRangeBounds =>
	customEnd ? { maxDate: customEnd, minDate: subWeeks(customEnd, CUSTOM_RANGE_MAX_WEEKS) } : {};

export const customRangeEndBounds = (customStart: Date | undefined): CustomRangeBounds =>
	customStart ? { maxDate: addWeeks(customStart, CUSTOM_RANGE_MAX_WEEKS), minDate: customStart } : {};

export const isoWeekKey = (point: IsoWeekPoint): string =>
	`${point.isoYear}-W${String(point.isoWeekNumber).padStart(2, "0")}`;

export const formatWeekMonthDay = (mondayDate: Date): string => format(mondayDate, "MMM d");

export const isSameIsoWeek = (a: IsoWeekPoint, b: IsoWeekPoint): boolean =>
	a.isoWeekNumber === b.isoWeekNumber && a.isoYear === b.isoYear;

export const isBeforeIsoWeek = (candidate: IsoWeekPoint, reference: IsoWeekPoint): boolean => {
	if (candidate.isoYear !== reference.isoYear) return candidate.isoYear < reference.isoYear;
	return candidate.isoWeekNumber < reference.isoWeekNumber;
};

export const isoWeekPointOffset = (point: IsoWeekPoint, weeksOffset: number): IsoWeekPoint =>
	toIsoWeekPoint(addWeeks(weekToDateRange(point.isoWeekNumber, point.isoYear).start, weeksOffset));

// An ISO year holds 52 or 53 weeks, so week-number subtraction miscounts across a year boundary.
export const weeksBetweenIsoWeeks = (from: IsoWeekPoint, to: IsoWeekPoint): number =>
	differenceInCalendarISOWeeks(
		weekToDateRange(to.isoWeekNumber, to.isoYear).start,
		weekToDateRange(from.isoWeekNumber, from.isoYear).start
	);

const ISO_WEEK_KEY_PATTERN = /^(\d{4})-W(\d{2})$/;

// The grid's scheduledWeek is a calendar week label ("2026-W38"), never an instant.
export const parseIsoWeekKey = (key: string): IsoWeekPoint | null => {
	const match = ISO_WEEK_KEY_PATTERN.exec(key);
	return match ? { isoWeekNumber: Number(match[2]), isoYear: Number(match[1]) } : null;
};
